import { logger } from "@/utils/logger";

import SecureStorage from './secureStorage';

export const RECENT_RESPONSE_KEY = 'recentAIResponse';
export const RECENT_API_CALL_KEY = 'recentOpenAICall';
export const RECENT_FULL_RESPONSE_KEY = 'recentOpenAIFullResponse';

export const saveRecentResponse = async (response: string): Promise<void> => {
  try {
    const timestamp = new Date().toISOString();
    const id = Date.now().toString();
    
    logger.log('🔥 saveRecentResponse called at:', timestamp);
    
    // Store in localStorage with timestamp and unique ID
    const dataToStore = {
      response: response,
      timestamp: timestamp,
      id: id
    };
    
    await SecureStorage.setItem(RECENT_RESPONSE_KEY, dataToStore);
    logger.log('🔥 Data stored securely with key:', RECENT_RESPONSE_KEY);
    logger.log('🔥 Stored with ID:', id, 'and timestamp:', timestamp);
    
    // Immediate verification
    const immediateCheck = await SecureStorage.getItem(RECENT_RESPONSE_KEY);
    logger.log('🔥 Immediate verification - data exists:', !!immediateCheck);
    
    if (immediateCheck) {
      logger.log('🔥 Immediate verification - timestamp:', immediateCheck.timestamp);
      logger.log('🔥 Immediate verification - ID:', immediateCheck.id);
      logger.log('🔥 Immediate verification - response length:', immediateCheck.response?.length || 0);
    }
  } catch (error) {
    console.error('❌ Failed to save recent response:', error);
  }
};

export const getRecentResponse = async (): Promise<string | null> => {
  try {
    const callTime = new Date().toISOString();
    logger.log('🔥 getRecentResponse called at:', callTime);
    
    const storedData = await SecureStorage.getItem(RECENT_RESPONSE_KEY);
    logger.log('🔥 Raw data from localStorage exists:', !!storedData);
    
    if (!storedData) {
      logger.log('🔥 No stored data found in localStorage');
      return null;
    }
    
    logger.log('🔥 Retrieved data from SecureStorage:', {
      hasResponse: !!storedData.response,
      hasTimestamp: !!storedData.timestamp,
      hasId: !!storedData.id,
      responseLength: storedData.response?.length || 0,
      timestamp: storedData.timestamp,
      id: storedData.id
    });
    
    if (storedData.response && storedData.timestamp) {
      logger.log('🔥 Retrieved timestamped data from:', storedData.timestamp);
      logger.log('🔥 Retrieved data with ID:', storedData.id);
      logger.log('🔥 Response length:', storedData.response.length);
      logger.log('🔥 Response preview:', storedData.response.substring(0, 200));
      logger.log('🔥 Response last 100 chars:', storedData.response.substring(Math.max(0, storedData.response.length - 100)));
      return storedData.response;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Failed to get recent response:', error);
    return null;
  }
};

export const clearRecentResponse = async (): Promise<void> => {
  try {
    SecureStorage.removeItem(RECENT_RESPONSE_KEY);
    logger.log('🔥 Recent response cleared from storage at:', new Date().toISOString());
    
    // Verify it was cleared
    const checkAfterClear = await SecureStorage.getItem(RECENT_RESPONSE_KEY);
    logger.log('🔥 Verification after clear - data exists:', !!checkAfterClear);
  } catch (error) {
    console.error('❌ Failed to clear recent response:', error);
  }
};

// New functions for storing and retrieving API call and full response data
export const saveRecentAPICall = async (apiCall: any): Promise<void> => {
  try {
    const timestamp = new Date().toISOString();
    const dataToStore = {
      apiCall: apiCall,
      timestamp: timestamp
    };
    
    await SecureStorage.setItem(RECENT_API_CALL_KEY, dataToStore);
    logger.log('🔥 API call data stored securely');
  } catch (error) {
    console.error('❌ Failed to save recent API call:', error);
  }
};

export const saveRecentFullResponse = async (fullResponse: any): Promise<void> => {
  try {
    const timestamp = new Date().toISOString();
    const dataToStore = {
      fullResponse: fullResponse,
      timestamp: timestamp
    };
    
    await SecureStorage.setItem(RECENT_FULL_RESPONSE_KEY, dataToStore);
    logger.log('🔥 Full response data stored securely');
  } catch (error) {
    console.error('❌ Failed to save recent full response:', error);
  }
};

export const getRecentAPICall = async (): Promise<any | null> => {
  try {
    const storedData = await SecureStorage.getItem(RECENT_API_CALL_KEY);
    return storedData?.apiCall || null;
  } catch (error) {
    console.error('❌ Failed to get recent API call:', error);
    return null;
  }
};

export const getRecentFullResponse = async (): Promise<any | null> => {
  try {
    const storedData = await SecureStorage.getItem(RECENT_FULL_RESPONSE_KEY);
    return storedData?.fullResponse || null;
  } catch (error) {
    console.error('❌ Failed to get recent full response:', error);
    return null;
  }
};
